package scan

import kotlin.js.Promise
import kotlin.js.json
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.dom.HTMLVideoElement

/**
 * The back camera in a `<video>` (ADR-0004 §8). Call it from a tap: iOS Safari only starts the
 * camera from a user gesture, and the element must be `playsinline` and `muted`.
 * Both test phones give 1080 × 1920 portrait video, as in the Frame corpus.
 */
suspend fun startCamera(video: HTMLVideoElement) {
    val constraints = json(
        "video" to json(
            "facingMode" to json("ideal" to "environment"),
            "width" to json("ideal" to 1920),
            "height" to json("ideal" to 1080),
        ),
        "audio" to false,
    )
    val mediaDevices: dynamic = window.navigator.asDynamic().mediaDevices
        ?: error("no camera API here: the page needs HTTPS and a browser with getUserMedia")
    video.srcObject = (mediaDevices.getUserMedia(constraints) as Promise<dynamic>).await()
    (video.play() as Promise<*>).await()
}

fun stopCamera(video: HTMLVideoElement) {
    val stream: dynamic = video.srcObject ?: return
    (stream.getTracks() as Array<dynamic>).forEach { it.stop() }
    video.srcObject = null
}

/** Whether the camera has stopped by itself, e.g. iOS ending the track while the tab was in the background. */
fun cameraEnded(video: HTMLVideoElement): Boolean {
    val stream: dynamic = video.srcObject ?: return true
    return (stream.getVideoTracks() as Array<dynamic>).none { it.readyState == "live" }
}

/** Whether [e], from [startCamera], means she said no to the camera (or Safari did, from its settings). */
fun cameraDenied(e: Throwable): Boolean = e.asDynamic().name == "NotAllowedError"

/** [e], from [startCamera], for an error panel: the DOMException's name, then its message. */
fun describeCameraError(e: Throwable): String = "camera: ${e.asDynamic().name ?: ""} ${e.message ?: e}"
